import React, { useState, useEffect } from 'react';
import API from '../api';
import SEO from "../components/SEO";

const AlumniApplyPage = () => {
    const [form, setForm] = useState({
        fullName: '',
        email: '',
        graduationYear: '',
        degree: '',
        department: '',
        phone: '',
        address: '',
    });
    const [profileImage, setProfileImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [status, setStatus] = useState(null);

    // Ensure the page scrolls to top whenever this page is navigated to or the id changes
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && window?.scrollTo) {
                // instant jump to top to avoid flashing at previous scroll position
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
        } catch (e) {
            // fallback: simple assignment
            try {
                window.scrollTo(0, 0);
            } catch (err) {
                // ignore in environments without window
            }
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                alert('Image size should not exceed 5MB');
                return;
            }
            // Validate file type
            if (!file.type.match(/image\/(jpg|jpeg|png|gif)/)) {
                alert('Please select a valid image file (JPG, PNG, or GIF)');
                return;
            }
            setProfileImage(file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('submitting');
        
        // Create FormData to handle file upload
        const formData = new FormData();
        Object.keys(form).forEach(key => {
            formData.append(key, form[key]);
        });
        
        // Add profile image if selected
        if (profileImage) {
            formData.append('profileImage', profileImage);
        }

        try {
            if (API && API.post) {
                await API.post('/alumni/register', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            } else {
                console.log('Alumni register payload', formData);
            }
            setStatus('success');
            // Reset form
            setForm({
                fullName: '',
                email: '',
                graduationYear: '',
                degree: '',
                department: '',
                phone: '',
                address: '',
            });
            setProfileImage(null);
            setImagePreview(null);
        } catch (err) {
            console.error(err);
            setStatus('error');
        }
    };

    const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://africaresearchuniversity.com/alumni/apply';
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        'name': 'Register as Alumni - Africa Research University',
        'description': 'Register to join the Africa Research University alumni network.',
        'url': pageUrl
    };

    return (
        <section className="min-h-screen py-16 bg-gray-50">
            <SEO title="Register as Alumni — Africa Research University" description="Register to join the Africa Research University alumni network." image="/assets/img/arulogo.png" url={pageUrl} jsonLd={jsonLd} />
            <div className="max-w-3xl my-16 mx-auto px-6">
                <div className="flex items-center mb-6">
                    <a href="/alumni?tab=get-involved" className="flex items-center text-blue-600 hover:text-blue-800">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back to Alumni
                    </a>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold mb-4">Register as Alumni</h1>
                <p className="text-gray-600 mb-6">Please fill out the form to register as an alumnus/alumna.</p>

                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input name="fullName" value={form.fullName} onChange={handleChange} placeholder="Full name" className="p-3 border rounded" required />
                        <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email" className="p-3 border rounded" required />
                        <input name="graduationYear" value={form.graduationYear} onChange={handleChange} placeholder="Graduation year" className="p-3 border rounded" />
                        <input name="degree" value={form.degree} onChange={handleChange} placeholder="Degree" className="p-3 border rounded" />
                        <input name="department" value={form.department} onChange={handleChange} placeholder="Department" className="p-3 border rounded" />
                        <input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone" className="p-3 border rounded" />
                        <textarea name="address" value={form.address} onChange={handleChange} placeholder="Address" className="p-3 border rounded md:col-span-2" />
                        
                        {/* Profile Image Upload */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Profile Picture (Optional)
                            </label>
                            <input 
                                type="file" 
                                accept="image/*" 
                                onChange={handleImageChange}
                                className="w-full p-2 border rounded"
                            />
                            <p className="text-xs text-gray-500 mt-1">Maximum file size: 5MB. Supported formats: JPG, PNG, GIF</p>
                            {imagePreview && (
                                <div className="mt-3">
                                    <img 
                                        src={imagePreview} 
                                        alt="Preview" 
                                        className="w-32 h-32 object-cover rounded-lg border-2 border-gray-300"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="mt-4 flex items-center gap-3">
                        <button type="submit" className="px-5 py-2 bg-green-600 text-white rounded">Submit Application</button>
                        {status === 'submitting' && <span className="text-sm">Submitting...</span>}
                        {status === 'success' && <span className="text-sm text-green-600">Application submitted</span>}
                        {status === 'error' && <span className="text-sm text-red-600">Submission failed</span>}
                    </div>
                </form>
            </div>
        </section>
    );
};

export default AlumniApplyPage;
